import { Component, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs';

@Component({
  selector: 'app-implemented-datamigration',
  imports: [CommonModule],
  standalone: true,
  templateUrl: './implemented-datamigration.component.html',
  styleUrl: './implemented-datamigration.component.scss'
})
export class ImplementedDatamigrationComponent implements OnInit, OnDestroy {
  private jsonLdScript: any;
  constructor(private router: Router, private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  nav(url: any) {
    console.log(url);
    this.router.navigateByUrl(url)
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map(() => this.activatedRoute),
      map((route) => {
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
      filter((route) => route.outlet === 'primary'),
      mergeMap((route: { data: any; }) => route.data)
    )
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const dataMigrationSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/implemented-datamigration",
          "url": "https://www.kalpitatechnologies.com/kalpita-case-studies/implemented-datamigration",
          "name": "Data Migration & Integrated Data Warehouse Implementation | Kalpita Case Study",
          "description": "Kalpita Technologies implemented cloud-based data migration and an Enterprise Data Warehouse (EDW) for a top-tier US insurance provider.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-01-15"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/implemented-datamigration#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/case-study", "name": "Case Studies" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/implemented-datamigration", "name": "Data Migration" } }
          ]
        },
        {
          "@type": "Article",
          "headline": "Data Migration & Integrated Data Warehouse Implementation for Informed Business Decisions",
          "articleBody": "Kalpita Technologies enabled a top-tier US insurance provider to modernize its data ecosystem by implementing a cloud-based data migration strategy and building an Enterprise Data Warehouse (EDW)...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-01-15",
          "image": "https://www.kalpitatechnologies.com/assets/resources/casestudies/datamigration-computer.webp"
        },
        {
          "@type": "HowTo",
          "name": "How to Implement Cloud Data Migration and an Enterprise Data Warehouse",
          "step": [
            { "@type": "HowToStep", "name": "Assess Limitations", "text": "Identify challenges in the existing system including complexity and delays." },
            { "@type": "HowToStep", "name": "Design Data Models", "text": "Design scalable data models to unify disparate data sources." },
            { "@type": "HowToStep", "name": "Migrate to Cloud", "text": "Migrate business-critical data to a cloud-based environment using Azure." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-case-studies/implemented-datamigration#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What was the main objective of the data migration project?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The main objective was to migrate business-critical data to a secure, scalable cloud environment and build an integrated Enterprise Data Warehouse."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(dataMigrationSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
}
